import * as zod from 'zod';

import { validateRobotHardwareMapping } from '@sb/ai/helpers';
import {
  HardwareConfigEquipmentMapping,
  ParsedModelMetadataPayload,
} from '@sb/ai/types';
import type { HardwareConfigSchema } from '@sb/ai/types';
import { ActionRequiredError, Step } from '@sb/remote-control/types';

import { buildConditional } from '../util/conditionalsBuilder';

/**
 * Base configuration for completion detection methods.
 * - For learned completion/failure: threshold is the model prediction confidence (0-1)
 * - For movement completion: threshold is the position delta in mm (e.g., 5 = 5mm)
 */
const CompletionConfig = zod.object({
  enabled: zod.boolean().default(false),
  threshold: zod.number(),
  consecutiveCount: zod.number().int(),
});

/**
 * Default values for completion prediction configuration
 */
export const DEFAULT_LEARNED_THRESHOLD = 0.75;
export const DEFAULT_LEARNED_CONSECUTIVE_COUNT = 2;
export const DEFAULT_LEARNED_FAILURE_THRESHOLD = 0.25;
export const DEFAULT_LEARNED_FAILURE_CONSECUTIVE_COUNT = 3;
export const DEFAULT_MOVEMENT_THRESHOLD_MM = 5;
export const DEFAULT_MOVEMENT_CONSECUTIVE_COUNT = 3;
export const DEFAULT_MOVEMENT_ROTATION_THRESHOLD_DEG = 5;
export const DEFAULT_MOVEMENT_GRIPPER_THRESHOLD = 0.1;

/**
 * Default values for inference configuration
 */
export const DEFAULT_FREQUENCY = 10;
export const DEFAULT_STEPS_PER_INFERENCE = 2;
export const DEFAULT_OBS_HORIZON = 1;

/**
 * Default timeout (seconds) seeded when a user adds the Timeout end condition.
 */
export const DEFAULT_TIMEOUT_SECONDS = 30;

/**
 * Sentinel timeout (seconds) forwarded to the routine runner when the step has
 * no explicit Timeout end condition. The runner contract requires a numeric
 * timeout, so this large value (365 days) effectively means "no timeout" and
 * lets the other end conditions drive completion.
 */
export const NO_TIMEOUT_SENTINEL_SECONDS = 31_536_000;

/**
 * Movement completion configuration.
 * threshold: position delta in mm. rotationThreshold: max rotation magnitude in degrees; if exceeded, movement completion fails.
 * gripperThreshold: if gripper delta across horizon exceeds this, movement completion fails. Default 0.1.
 */
const MovementCompletionConfig = CompletionConfig.extend({
  rotationThreshold: zod
    .number()
    .default(DEFAULT_MOVEMENT_ROTATION_THRESHOLD_DEG), // degrees
  gripperThreshold: zod.number().default(DEFAULT_MOVEMENT_GRIPPER_THRESHOLD),
});

/**
 * Consolidated completion prediction configuration with type-specific defaults
 */
const CompletionPredictionConfig = zod.object({
  learnedCompletion: CompletionConfig.default({
    enabled: false,
    threshold: DEFAULT_LEARNED_THRESHOLD,
    consecutiveCount: DEFAULT_LEARNED_CONSECUTIVE_COUNT,
  }),
  learnedFailure: CompletionConfig.default({
    enabled: false,
    threshold: DEFAULT_LEARNED_FAILURE_THRESHOLD,
    consecutiveCount: DEFAULT_LEARNED_FAILURE_CONSECUTIVE_COUNT,
  }),
  movementCompletion: MovementCompletionConfig.default({
    enabled: false,
    threshold: DEFAULT_MOVEMENT_THRESHOLD_MM, // mm default
    consecutiveCount: DEFAULT_MOVEMENT_CONSECUTIVE_COUNT,
    rotationThreshold: DEFAULT_MOVEMENT_ROTATION_THRESHOLD_DEG, // degrees default
    gripperThreshold: DEFAULT_MOVEMENT_GRIPPER_THRESHOLD,
  }),
});

// NOTE: the `RunSkillStep` namespace name is retained for code-history
// continuity, but the persisted step kind is `RunLearnedModel` (renamed from
// the legacy `RunSkill`). Existing saved routines are migrated to the new kind
// by bot-migration 0069_rename_runskill_to_runlearnedmodel.
export namespace RunSkillStep {
  export const name = 'Run learned model';
  export const description = 'Run an AI learned model';
  export const librarySection = Step.LibrarySection.Control;
  export const argumentKind = 'RunLearnedModel';
  export const featureFlag = 'enableLearnedModels';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    modelPath: zod.string().nullable().default(null),
    // Persisted skill selection for the wizard. Not forwarded to the routine
    // runner — the runner only consumes modelPath. Knowing which skill the
    // step was built from lets the wizard seed modelPath from the skill's
    // current active checkpoint when the user first picks a skill, and
    // surface the checkpoint inline + via a dropdown override on reopen.
    skillId: zod.string().nullable().default(null),
    // null = no Timeout end condition (additive End Conditions list). New
    // steps start with no timeout; toRoutineRunner maps null to a sentinel.
    timeout: zod.number().nullable().default(null),
    gpuTaskRunnerId: zod.string().nullable().default(null),
    isBimanual: zod.boolean().default(false),
    isPrimary: zod.boolean().default(true),
    equipmentKeyToEquipmentId: zod.record(zod.string()).default({}),
    hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
    condition: zod.string().nullable().default(null),
    modelMetadata: ParsedModelMetadataPayload.nullable().default(null),
    completionPrediction: CompletionPredictionConfig.default({}),
    caption: zod.string().nullable().default(null),
    useGripperMask: zod.boolean().default(false),
    frequency: zod.number().default(10),
    stepsPerInference: zod.number().int().default(2),
    maxLinearSpeed: zod.number().nullable().default(null),
    obsHorizon: zod.number().int().default(1),
    actionExecutionMode: zod.string().default('overwrite'),
    actionInterpolationMethod: zod.string().default('pchip'),
    outputSmoothingAlpha: zod.number().default(1),
    robotControllerFrequency: zod.number().int().default(0),
    enablePredictionLogging: zod.boolean().default(false),
    lockedAxesPerRobot: zod
      .record(zod.string(), zod.array(zod.number().int().min(0).max(6)))
      .default({}),
    axisLockingFramePerRobot: zod
      .record(zod.string(), zod.enum(['world', 'wrist']))
      .default({}),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  /**
   * True when the step has at least one end condition. Used both to validate
   * the step (toRoutineRunner) and to drive the End Conditions list UI's
   * minimum-of-one rule.
   */
  export function hasAnyEndCondition(args: Arguments): boolean {
    const hasAlternativeCondition =
      args.condition != null &&
      args.condition !== '' &&
      args.condition !== '[]';

    return (
      args.timeout != null ||
      args.completionPrediction.learnedCompletion.enabled ||
      args.completionPrediction.learnedFailure.enabled ||
      args.completionPrediction.movementCompletion.enabled ||
      hasAlternativeCondition
    );
  }

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    if (!args.modelPath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Model path not specified',
        fieldId: 'modelPath',
      });
    }

    if (args.gpuTaskRunnerId == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No GPU Task runner specified',
        fieldId: 'gpuTaskRunnerId',
      });
    }

    const equipmentConfig = equipment.find(
      (e) => e.id === args.gpuTaskRunnerId,
    );

    if (!equipmentConfig) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${args.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'gpuTaskRunnerId',
      });
    }

    // Validate hardware configuration mapping before allowing routine to run
    if (args.isPrimary) {
      const robots: HardwareConfigSchema['robots'] = ((
        args.modelMetadata?.trainingRun?.modelArchitecture?.inputHardwareConfig
          ?.config as unknown as HardwareConfigSchema | undefined
      )?.robots ?? {}) as HardwareConfigSchema['robots'];

      const robotKeys = Object.keys(robots);

      // For single robot (non-bimanual): check primary robot only
      // For bimanual: check all robots (primary and secondary)
      const robotsToValidate = args.isBimanual
        ? robotKeys
        : robotKeys.filter((robotKey) => robots[robotKey].isPrimary);

      const unconfiguredRobots = robotsToValidate.filter((robotKey) => {
        const robotEquipmentMapping =
          args.hardwareConfigEquipmentMapping[robotKey];

        // If no mapping exists for this robot, it's unconfigured
        if (!robotEquipmentMapping) {
          return true;
        }

        return validateRobotHardwareMapping({
          robot: robots[robotKey],
          robotEquipmentMapping,
          botHardwareMap: args.modelMetadata?.botHardware,
        });
      });

      if (unconfiguredRobots.length > 0) {
        const inferenceType = args.isBimanual
          ? 'bimanual inference'
          : 'inference';

        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Hardware configuration mapping needs to be completed for all robot(s) before beginning ${inferenceType}`,
          fieldId: 'hardwareConfigEquipmentMapping',
        });
      }
    }

    // Only the primary drives when the run ends; a secondary bot inherits
    // end-of-run from the primary and has no end-condition UI (the panel's
    // EndConditionsSection is gated on isPrimary), so it must not require one.
    if (args.isPrimary && !hasAnyEndCondition(args)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'At least one end condition must be selected',
        fieldId: 'endConditions',
      });
    }

    if (args.timeout != null && args.timeout <= 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Timeout must be greater than 0',
        fieldId: 'timeout',
      });
    }

    const condition = args.condition
      ? buildConditional(JSON.parse(args.condition))
      : null;

    return {
      ...stepData,
      stepKind: 'RunLearnedModel',
      args: {
        gpuTaskRunnerId: args.gpuTaskRunnerId,
        modelPath: args.modelPath,
        timeout: args.timeout ?? NO_TIMEOUT_SENTINEL_SECONDS,
        isBimanual: args.isBimanual,
        isPrimary: args.isPrimary,
        equipmentKeyToEquipmentId: args.equipmentKeyToEquipmentId,
        hardwareConfigEquipmentMapping: args.hardwareConfigEquipmentMapping,
        condition,
        completionPrediction: args.completionPrediction,
        caption: args.caption,
        useGripperMask: args.useGripperMask,
        frequency: args.frequency,
        stepsPerInference: args.stepsPerInference,
        maxLinearSpeed: args.maxLinearSpeed,
        obsHorizon: args.obsHorizon,
        actionExecutionMode: args.actionExecutionMode,
        actionInterpolationMethod: args.actionInterpolationMethod,
        outputSmoothingAlpha: args.outputSmoothingAlpha,
        robotControllerFrequency: args.robotControllerFrequency,
        enablePredictionLogging: args.enablePredictionLogging,
        lockedAxesPerRobot: args.lockedAxesPerRobot,
        axisLockingFramePerRobot: args.axisLockingFramePerRobot,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    const modelName = args?.modelMetadata?.trainingRun?.name;

    if (!(args.modelPath || modelName)) {
      return 'with a model';
    }

    if (modelName) {
      return `with model "${modelName}"`;
    }

    const modelPathParts = args.modelPath?.split('/') || [];

    if (modelPathParts.length === 0) {
      return 'with a model';
    }

    return `with model "${modelPathParts[modelPathParts.length - 1]}"`;
  };
}

RunSkillStep satisfies Step.StepKindInfo;
